import { useEffect, useState } from "react";
import { BellRing, Check, Loader2, MessageCircle, MoonStar } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const FR_DAYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function frDate(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return `${FR_DAYS[d.getDay()]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
}

function waPhone(phone) {
  let digits = (phone || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = `32${digits.slice(1)}`;
  return digits;
}

function reminderText(a, dateLabel, address, category, presenceLink) {
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(`${address}, Belgique`)}`;
  const cat = (category || "").toLowerCase();
  let consignes = "";
  if (cat.includes("laser")) {
    consignes =
      "Pour préparer votre séance laser, 24h avant le rendez-vous :\n" +
      "🪒 Rasez les zones qui seront traitées (pas de cire ni d'épilateur)\n" +
      "🧴 Le jour du rendez-vous : pas de crème, parfum ni déodorant sur les zones\n" +
      "☀️ Évitez le soleil et les UV avant la séance";
  } else if (cat.includes("lectrolyse")) {
    consignes =
      "Pour préparer votre séance :\n" +
      "🧼 Venez avec une peau propre, sans maquillage ni parfum\n" +
      "☀️ Pas d'exposition au soleil ni d'autobronzant avant la séance\n" +
      "💧 Hydratez bien votre peau les jours précédents pour plus de confort";
  } else if (cat.includes("regard") || cat.includes("cil") || cat.includes("brow")) {
    consignes =
      "Pour préparer votre soin :\n" +
      "👁️ Ne venez pas maquillée\n" +
      "🧴 Pas d'huile ni de crème sur les zones à traiter";
  }
  return (
    `Bonjour ${a.name} 🌸\n\n` +
    `Petit rappel de votre rendez-vous chez Nadou Skin :\n` +
    `📅 ${dateLabel} à ${a.time}\n` +
    `✨ ${a.service_name}\n\n` +
    `📍 ${address} — pensez à bien indiquer Saint-Gilles dans votre GPS\n` +
    `🗺️ Plan : ${mapsUrl}\n\n` +
    (consignes ? `${consignes}\n\n` : "") +
    `✅ Confirmez votre présence en 1 clic : ${presenceLink}\n\n` +
    `À demain ! 🤍\nNadou`
  );
}

export const Reminders = () => {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ categories: {}, address: "Chaussée de Waterloo 80, 1060 Saint-Gilles" });
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api.get("/admin/reminders").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, []);

  useEffect(() => {
    Promise.all([api.get("/admin/services"), api.get("/admin/settings")])
      .then(([svcRes, setRes]) => {
        const categories = {};
        svcRes.data.services.forEach((s) => { categories[s.id] = s.category; });
        setMeta((m) => ({ categories, address: setRes.data.address || m.address }));
      })
      .catch(() => {});
  }, []);

  const send = async (a) => {
    const presenceLink = `${window.location.origin}/presence/${a.token}`;
    const url = `https://wa.me/${waPhone(a.phone)}?text=${encodeURIComponent(
      reminderText(a, frDate(data.date), meta.address, meta.categories[a.service_id], presenceLink)
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setBusyId(a.id);
    try {
      await api.post(`/admin/appointments/${a.id}/reminder`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!data) {
    return (
      <div className="flex items-center gap-3 text-sm text-espresso/55 py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div data-testid="admin-reminders-tab">
      <div className="bg-white border border-taupe/20 rounded-2xl p-6 flex items-start gap-4">
        <MoonStar className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
        <p className="text-sm text-espresso/65 font-light leading-relaxed">
          Chaque soir, envoyez le rappel aux clientes de <span className="font-medium text-espresso">demain ({frDate(data.date)})</span>.
          Le bouton ouvre WhatsApp avec le message déjà rédigé — il ne vous reste qu'à appuyer sur envoyer.
        </p>
      </div>

      <div className="mt-6 space-y-3">
        {data.appointments.length === 0 ? (
          <div className="text-center py-16 text-espresso/45" data-testid="reminders-empty">
            <BellRing className="w-8 h-8 mx-auto mb-3 text-taupe/50" strokeWidth={1.5} />
            <p className="text-sm">Aucun rendez-vous confirmé demain. Rien à rappeler.</p>
          </div>
        ) : (
          data.appointments.map((a) => (
            <div
              key={a.id}
              data-testid={`reminder-card-${a.id}`}
              className="bg-white border border-taupe/20 rounded-2xl p-5 flex flex-wrap items-center gap-4 justify-between"
            >
              <div className="min-w-[200px]">
                <p className="font-display text-xl text-espresso">{a.name}</p>
                <p className="text-xs text-espresso/55 mt-1">{a.service_name} · {a.time} · {a.phone}</p>
              </div>
              {a.presence_confirmed && (
                <span
                  className="rounded-full bg-[#4A6B53]/10 text-[#4A6B53] px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5"
                  data-testid={`presence-badge-${a.id}`}
                >
                  <Check className="w-3.5 h-3.5" /> Présence confirmée
                </span>
              )}
              {a.reminder_sent ? (
                <span
                  className="rounded-full bg-[#4A6B53]/10 text-[#4A6B53] px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5"
                  data-testid={`reminder-done-${a.id}`}
                >
                  <Check className="w-3.5 h-3.5" /> Rappel envoyé
                </span>
              ) : (
                <button
                  data-testid={`reminder-send-${a.id}`}
                  disabled={busyId === a.id}
                  onClick={() => send(a)}
                  className="rounded-full bg-[#4A6B53] text-white px-5 py-2.5 text-xs tracking-[0.08em] uppercase inline-flex items-center gap-2 hover:brightness-110 hover:-translate-y-0.5 transition-[filter,transform] duration-300 disabled:opacity-50"
                >
                  {busyId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  Envoyer le rappel
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
